import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { Container } from 'components/gruenland/atoms/container/Container';
import useCloseFlyouts from 'helpers/hooks/useCloseFlyouts';
import { useFormat } from 'helpers/hooks/useFormat';
import { sendGa4ViewCartEvent } from 'helpers/utils/ga4';
import CartSummary from '../cart-summary/CartSummary';
import CartContent from './components/cart-content';
import ReservationFailedAlert from './components/ReservationFailedAlert';
import { CartProps } from './types';
import CheckoutButton from '../cart-summary/CheckoutButton';
import { CheckoutButtonProps } from '../cart-summary/types';

const Cart = ({
  isEmpty,
  hasOutOfStockItems,
  onApplyDiscountCode,
  onRemoveDiscountCode,
  login,
  requestConfirmationEmail,
  requestPasswordReset,
  ...props
}: CartProps) => {
  const { formatMessage } = useFormat({ name: 'gruenland' });

  const closeFlyouts = useCloseFlyouts();

  const defaultCheckoutButtonProps: CheckoutButtonProps = useMemo(() => {
    return {
      text: formatMessage({ id: 'checkout.go', defaultMessage: 'Procedi al pagamento' }),
      link: '/checkout',
      onClick: closeFlyouts,
      disabled: isEmpty || hasOutOfStockItems,
    };
  }, [closeFlyouts, formatMessage, hasOutOfStockItems, isEmpty]);

  useEffect(() => {
    props.cart && sendGa4ViewCartEvent(props.cart);
  }, [props.cart]);

  const searchParams = useSearchParams();
  const reservationFailed = searchParams.get('error') === 'reservationFailed';

  return (
    <Container>
      {reservationFailed && <ReservationFailedAlert />}
      <div className="flex flex-col lg:flex-row lg:items-start lg:gap-20">
        <CartContent className="grow" isEmpty={isEmpty} {...props} />
        {!isEmpty && (
          <CartSummary
            className="lg:w-[30%]"
            button={<CheckoutButton className="mt-5" {...defaultCheckoutButtonProps} />}
            discounts={props.cart?.discountCodes ?? []}
            onApplyDiscountCode={onApplyDiscountCode}
            onRemoveDiscountCode={onRemoveDiscountCode}
            login={login}
            requestConfirmationEmail={requestConfirmationEmail}
            requestPasswordReset={requestPasswordReset}
          />
        )}
      </div>
    </Container>
  );
};

export default Cart;
